import React from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';

import type {AppState} from '@stores/AppState';
import type {ClientUser, TaskListUnit} from '../../../../API';

import {ContextMenu} from '../../../components/ContextMenu/ContextMenu';
import {UserCard} from '../../../components/UserCard';
import {TaskListUnitAvatar} from '../../../components/Avatar/TaskListUnitAvater';
import {ContextMenuInterface} from '../ContextMenuInterface';

import './AccessRoleGroupContextMenu.css';

interface AccessRoleMembersContextMenuOwnProps {
    roleId: string;
    users: ClientUser[];
}

interface AccessRoleLocationsContextMenuOwnProps {
    roleId: string;
    taskListUnits: TaskListUnit[];
    includeFutureLocations?: boolean;
}

export type AccessRoleMembersContextMenuProps = AccessRoleMembersContextMenuOwnProps & ContextMenuInterface;
export type AccessRoleLocationsContextMenuProps = AccessRoleLocationsContextMenuOwnProps & ContextMenuInterface;

const getTargetBoundingBox = (targetDom: HTMLElement) => () => targetDom.getBoundingClientRect();

const openAccessRoleGroupContextMenu = (
    appState: AppState,
    type: 'access-role-members' | 'access-role-locations',
    id: string,
    params: AccessRoleMembersContextMenuOwnProps | AccessRoleLocationsContextMenuOwnProps,
    event: React.MouseEvent<HTMLElement>
) => {
    const targetDom = event.currentTarget;

    appState.addAdditionalContextMenu({
        type,
        id,
        withOverlay: true,
        isDark: false,
        menuPlacement: 'block-end',
        menuAlignment: 'center',
        VERTICAL_SHIFT: 0,
        HORIZONTAL_SHIFT: 0,
        withPointer: false,
        minWidth: 320,
        maxWidth: 360,
        params,
        getTargetBoundingBox: getTargetBoundingBox(targetDom),
    });
};

export const openAccessRoleMembersContextMenu = (
    appState: AppState,
    params: AccessRoleMembersContextMenuOwnProps,
    event: React.MouseEvent<HTMLElement>
) => {
    openAccessRoleGroupContextMenu(
        appState,
        'access-role-members',
        params.roleId,
        params,
        event
    );
};

export const openAccessRoleLocationsContextMenu = (
    appState: AppState,
    params: AccessRoleLocationsContextMenuOwnProps,
    event: React.MouseEvent<HTMLElement>
) => {
    openAccessRoleGroupContextMenu(
        appState,
        'access-role-locations',
        params.roleId,
        params,
        event
    );
};

export const AccessRoleMembersContextMenu = observer(
    ({handleClose, isDark, users}: AccessRoleMembersContextMenuProps) => {
        return (
            <ContextMenu
                isDark={isDark}
                handleClose={handleClose}
                maxHeight={500}
                items={users.map(user => ({
                    className: 'AccessRoleGroupContextMenu__item',
                    text: (
                        <UserCard
                            user={user}
                            size={36}
                            isEmail={false}
                        />
                    ),
                }))}
            />
        );
    }
);

export const AccessRoleLocationsContextMenu = observer(
    ({handleClose, isDark, taskListUnits, includeFutureLocations}: AccessRoleLocationsContextMenuProps) => {
        return (
            <ContextMenu
                isDark={isDark}
                handleClose={handleClose}
                maxHeight={500}
                items={[
                    ...(includeFutureLocations
                        ? [{
                            className: 'AccessRoleGroupContextMenu__futureItem',
                            text: intl.get('accesses.summary.allFuture').d('All future'),
                        }]
                        : []),
                    ...taskListUnits.map(taskListUnit => ({
                        className: 'AccessRoleGroupContextMenu__item',
                        text: (
                            <div className="AccessRoleGroupContextMenu__location">
                                <TaskListUnitAvatar
                                    taskListUnit={taskListUnit}
                                    size={36}
                                />
                                <div className="AccessRoleGroupContextMenu__locationName body-large">
                                    {taskListUnit.name || intl.get('common.untitled').d('Untitled')}
                                </div>
                            </div>
                        ),
                    })),
                ]}
            />
        );
    }
);
